import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api as releaseNotesApi, ReleaseNotes } from "../../../services/apis/release-notes";

const RELEASE_NOTES_QUERY_KEY = ["release-notes"];

export function useReleaseNotes() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: RELEASE_NOTES_QUERY_KEY,
    queryFn: () => releaseNotesApi.getReleaseNotes(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const acknowledge = useMutation({
    mutationFn: (version: string) => releaseNotesApi.acknowledgeReleaseNotes(version),
    // Persist the acknowledgement in the query cache immediately rather than relying
    // on a refetch. A refetch can fail or return eventually-consistent stale
    // `acknowledged: false` data, which would re-show the card after navigating away
    // and back to the dashboard.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: RELEASE_NOTES_QUERY_KEY });
      const previous = queryClient.getQueryData<ReleaseNotes>(RELEASE_NOTES_QUERY_KEY);
      queryClient.setQueryData<ReleaseNotes>(RELEASE_NOTES_QUERY_KEY, (old) =>
        old ? { ...old, acknowledged: true } : old
      );
      return { previous };
    },
    onError: (_error, _version, context) => {
      if (context?.previous) {
        queryClient.setQueryData(RELEASE_NOTES_QUERY_KEY, context.previous);
      }
    },
  });

  return { ...query, acknowledge };
}
